// Block-explorer resolvers for online Bitcoin chain confirmation.
//
// The verifier needs to confirm block N exists on the longest chain AND
// that its merkle_root matches what the OTS proof commits to.
//
// IMPORTANT — endianness contract:
// Block explorers (mempool.space, blockstream.info, Bitcoin Core JSON-RPC)
// return merkle_root in DISPLAY order — the byte-reversed form you see in
// block explorers. The OTS protocol's BTC attestation walker emits the
// running msg in INTERNAL order — what the block header itself commits to.
// `BlockResolver` returns merkle_root in INTERNAL order so the verifier's
// equality check is straightforward. Resolvers that pull from explorers
// MUST byte-reverse before returning. The bundled `mempoolSpaceResolver`
// does this; custom resolvers must follow the same convention.

export type BlockResolver = (
  height: number,
) => Promise<{ merkleRootHex: string }>;

const MEMPOOL_BASE = "https://mempool.space/api";
const FETCH_TIMEOUT_MS = 8_000;

// Reverse a hex string by byte (pair of characters). "ab" → "ab"; "abcd" → "cdab".
function reverseHexBytes(hex: string): string {
  const lower = hex.toLowerCase();
  const pairs: string[] = [];
  for (let i = 0; i < lower.length; i += 2) {
    pairs.push(lower.slice(i, i + 2));
  }
  return pairs.reverse().join("");
}

function fetchWithTimeout(url: string): Promise<Response> {
  return fetch(url, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });
}

// Default resolver: hit mempool.space's public API. Two HTTP roundtrips
// (height → hash, hash → block info). Mempool's `merkle_root` field is in
// DISPLAY order; we byte-reverse to internal order before returning so the
// verifier's equality compare is direct.
export const mempoolSpaceResolver: BlockResolver = async (height) => {
  const hashRes = await fetchWithTimeout(
    `${MEMPOOL_BASE}/block-height/${height}`,
  );
  if (!hashRes.ok) {
    throw new Error(
      `btc-explorer: height lookup HTTP ${hashRes.status} for block ${height}`,
    );
  }
  const hash = (await hashRes.text()).trim();

  const blockRes = await fetchWithTimeout(`${MEMPOOL_BASE}/block/${hash}`);
  if (!blockRes.ok) {
    throw new Error(
      `btc-explorer: block lookup HTTP ${blockRes.status} for hash ${hash}`,
    );
  }
  const block = (await blockRes.json()) as { merkle_root: unknown };
  if (
    typeof block.merkle_root !== "string" ||
    !/^[0-9a-fA-F]{64}$/.test(block.merkle_root)
  ) {
    throw new Error(
      `btc-explorer: block ${height} has malformed merkle_root`,
    );
  }
  return { merkleRootHex: reverseHexBytes(block.merkle_root) };
};

